import PropTypes from 'prop-types'
import { Badge } from './ui/badge'
import { ButtonSwitch } from './ui/ButtonSwitch'
import { formatDollarAmountWithCents, formatDollarAmountWhole, formatPoints } from '../utils/string-format-utils'

const DEFAULT_COLORS = [
  'var(--Fasten-Midnight-Drive, #011C25)',
  'var(--Fasten-Dream-Car-Green, #084045)',
  'var(--Fasten-Velocity-Aqua, #26C7B8)',
  'var(--Fasten-Joyride-Coral, #FC745D)',
  'var(--Fasten-Rally-Gold, #FFA500)',
  'var(--Fasten-Gear-Neutral, #ECE8DE)',
  'var(--Grays-Gray-5, #ECECEC)',
]

// Shared logic for processing categories
function useProcessedCategories(categories) {
  const total = categories.reduce((sum, cat) => sum + cat.dollarAmount, 0)

  // Assign colors in order, repeating if necessary
  const coloredCategories = categories.map((cat, i) => ({
    ...cat,
    color: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
  }))

  // Calculate the left offset (in %) for the center of each bar segment
  let currentLeft = 0
  const segments = coloredCategories.map((cat, i) => {
    const width = (cat.dollarAmount / total) * 100
    const center = currentLeft + width / 2
    const segment = { ...cat, width, center, index: i }
    currentLeft += width
    return segment
  })

  return { total, segments }
}

// Desktop Legend Item Component
function DesktopLegendItem({ name, dollarAmount, pointMultiplier, isAbove, center, legendGap }) {
  const points = dollarAmount * pointMultiplier
  
  return (
    <div
      style={{
        left: `calc(${center}% - 60px)`,
        width: 120,
        top: isAbove ? 0 : undefined,
        bottom: isAbove ? undefined : 0,
        marginBottom: isAbove ? legendGap : undefined,
        marginTop: !isAbove ? legendGap : undefined,
      }}
      className="absolute flex flex-col items-center"
    >
      <span className="text-[12px] font-medium font-[500]" style={{ color: 'var(--Grays-Gray, #5D5D5D)' }}>{name}</span>
      <span className="text-[20px] font-semibold font-[600] text-black">${formatDollarAmountWhole(dollarAmount)}</span>
      <div style={{ visibility: pointMultiplier > 0 ? 'visible' : 'hidden' }}>
        <Badge>{formatPoints(points)} pts</Badge>
      </div>
    </div>
  )
}

// Desktop Legend Line Component
function DesktopLegendLine({ isAbove, center, legendGap, barHeight }) {
  const lineHeight = `calc(50% - ${barHeight / 2}px - ${legendGap}px - 20px)`;
  const containerTop = isAbove ? `calc(50% - ${barHeight}px - 6px)` : `calc(50% + ${barHeight / 2}px)`;
  
  return (
    <div
      style={{
        position: 'absolute',
        left: `calc(${center}% - 5.5px)`,
        top: containerTop,
        width: 11,
        height: `calc(${lineHeight} + 11px)`,
        zIndex: 3,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: isAbove ? 11 : 0,
          transform: 'translateX(-50%)',
          width: 2,
          height: `calc(100% - 11px)`,
          background: 'var(--Fasten-Dream-Car-Green, #084045)',
          zIndex: 2,
        }}
      />
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="11"
        height="11"
        viewBox="0 0 11 11"
        fill="none"
        style={{
          position: 'absolute',
          left: 0,
          top: isAbove ? 0 : `calc(100% - 11px)`,
        }}
      >
        <circle cx="5.5" cy="5.5" r="5.5" fill="var(--Fasten-Dream-Car-Green, #084045)" />
      </svg>
    </div>
  );
}

// Mobile Legend Item Component
function MobileLegendItem({ name, dollarAmount, pointMultiplier, color }) {
  const points = dollarAmount * pointMultiplier
  
  return (
    <div className="flex items-center justify-between w-full py-2">
      <div className="flex items-center gap-3 flex-1">
        {/* Color bullet */}
        <div 
          className="w-3 h-3 rounded-full flex-shrink-0" 
          style={{ backgroundColor: color }}
        />
        {/* Name */}
        <span className="text-sm font-medium text-gray-600">{name}</span>
        {/* Dollar amount */}
        <span className="text-sm font-medium text-black">${formatDollarAmountWhole(dollarAmount)}</span>
      </div>
      {/* Point badge - right justified */}
      {pointMultiplier > 0 && (
        <Badge>{formatPoints(points)} pts</Badge>
      )}
    </div>
  )
}

// Shared Header Component
function HeaderSection({ total, showButtonSwitch = true, isMonthlyView, setIsMonthlyView }) {
  return (
    <div className="w-full mb-5 flex items-start">
      <div className="flex-shrink-0" style={{ width: '250px' }}>
        <h2 
          style={{
            color: 'black',
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: '32px',
            margin: 0,
            marginBottom: '8px',
          }}
        >
          Total Cost of Ownership
        </h2>
        <div
          style={{
            color: 'black',
            fontSize: '42px',
            fontWeight: 600,
            margin: 0,
          }}
        >
          ${formatDollarAmountWithCents(total)}
        </div>
      </div>
      {showButtonSwitch && (
        <div className="flex-1 pl-4 ml-auto max-w-[420px]">
          <ButtonSwitch 
            leftOption="Total Cost" 
            rightOption="Monthly Cost" 
            value={isMonthlyView} 
            onValueChange={setIsMonthlyView} 
          />
        </div>
      )}
    </div>
  )
}

// Shared Bar Component
function BarChart({ segments, height = '50px', showGaps = false }) {
  const segmentGap = showGaps ? 2 : 0
  
  return (
    <div
      className="w-full flex"
      style={{
        height,
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {segments.map((seg, i) => (
        <div
          key={seg.name}
          style={{
            width: showGaps 
              ? `calc(${seg.width}% - ${i < segments.length - 1 ? segmentGap : 0}px)`
              : `${seg.width}%`,
            background: seg.color,
            height: '100%',
            borderTopLeftRadius: i === 0 ? 12 : 0,
            borderBottomLeftRadius: i === 0 ? 12 : 0,
            borderTopRightRadius: i === segments.length - 1 ? 12 : 0,
            borderBottomRightRadius: i === segments.length - 1 ? 12 : 0,
            marginRight: showGaps && i < segments.length - 1 ? segmentGap : 0,
            transition: 'width 0.3s',
          }}
          className="h-full"
          title={`${seg.name}: $${formatDollarAmountWhole(seg.dollarAmount)}`}
        />
      ))}
    </div>
  )
}

// Desktop Layout Component
function DesktopLayout({ total, segments, isMonthlyView, setIsMonthlyView }) {
  const legendHeight = 40
  const legendGap = 70
  const barHeight = 50
  const containerHeight = legendHeight * 2 + barHeight + legendGap * 2
  const minSeparation = 10 // Minimum pixels between legend items

  // Calculate adjusted positions to prevent overlap
  const calculateAdjustedPositions = (segments) => {
    const adjustments = new Map() // Track adjustments for each segment
    const minEdgeMargin = 4 // Minimum margin from container edges in percentage (6% = 60px on each side)
    
    for (let i = 0; i < segments.length; i++) {
      for (let j = i + 1; j < segments.length; j++) {
        const seg1 = segments[i]
        const seg2 = segments[j]
        
        // Use original center positions for calculations
        const pos1 = seg1.center
        const pos2 = seg2.center
        
        // Calculate if legend items overlap
        const distance = Math.abs(pos2 - pos1)
        const minRequiredDistance = minSeparation // Just 5px minimum separation
        
        if (distance < minRequiredDistance) {
          // Calculate how much to push each item outward
          const pushAmount = (minRequiredDistance - distance) / 2
          
          // Track adjustments for each segment
          const currentAdjustment1 = adjustments.get(i) || 0
          const currentAdjustment2 = adjustments.get(j) || 0
          
          if (pos1 < pos2) {
            adjustments.set(i, currentAdjustment1 - pushAmount)
            adjustments.set(j, currentAdjustment2 + pushAmount)
          } else {
            adjustments.set(i, currentAdjustment1 + pushAmount)
            adjustments.set(j, currentAdjustment2 - pushAmount)
          }
        }
      }
    }
    
    // Apply adjustments and add edge protection
    return segments.map((seg, index) => {
      const adjustedCenter = seg.center + (adjustments.get(index) || 0)
      
      // Prevent item from going outside container bounds
      let finalCenter = adjustedCenter
      
      // Prevent left edge from going outside container (item is 120px wide, so center should be at least 6% from left)
      if (finalCenter < minEdgeMargin) {
        finalCenter = minEdgeMargin
      }
      
      // Prevent right edge from going outside container (item is 120px wide, so center should be at most 94% from left)
      if (finalCenter > (100 - minEdgeMargin)) {
        finalCenter = 100 - minEdgeMargin
      }
      
      return {
        ...seg,
        adjustedCenter: finalCenter
      }
    })
  }

  const adjustedSegments = calculateAdjustedPositions(segments)

  return (
    <div className="w-full flex flex-col pb-10">
      <HeaderSection total={total} showButtonSwitch={true} isMonthlyView={isMonthlyView} setIsMonthlyView={setIsMonthlyView} />

      {/* Bar and Legends Container */}
      <div className="w-full flex flex-col items-center gap-6 relative" style={{ height: containerHeight }}>
        {/* Bar, vertically centered */}
        <div
          className="w-full flex absolute left-0"
          style={{
            top: `calc(50% - ${barHeight / 2}px)`,
            height: `${barHeight}px`,
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          <BarChart segments={segments} height={`${barHeight}px`} showGaps={true} />
        </div>
        {/* Vertical lines from legend to bar */}
        {segments.map((seg, i) => {
          const isAbove = i % 2 === 1
          return <DesktopLegendLine key={seg.name + '-line'} isAbove={isAbove} center={seg.center} legendGap={legendGap} barHeight={barHeight} name={seg.name} />
        })}
        {/* Legends: above and below the bar */}
        {adjustedSegments.map((seg, i) => {
          const isAbove = i % 2 === 1
          return (
            <DesktopLegendItem
              key={seg.name + '-legend'}
              name={seg.name}
              dollarAmount={seg.dollarAmount}
              pointMultiplier={seg.pointMultiplier}
              isAbove={isAbove}
              center={seg.adjustedCenter || seg.center}
              legendGap={legendGap}
            />
          )
        })}
      </div>
    </div>
  )
}

// Mobile Layout Component
function MobileLayout({ total, segments, isMonthlyView, setIsMonthlyView }) {
  return (
    <div className="w-full flex flex-col">
      {/* Button Switch - Full width at top */}
      <div className="w-full mb-6 flex justify-center">
        <ButtonSwitch 
          leftOption="Total Cost" 
          rightOption="Monthly Cost" 
          value={isMonthlyView} 
          onValueChange={setIsMonthlyView} 
        />
      </div>

      {/* Total Cost of Ownership */}
      <div className="w-full mb-2">
        <h2 
          style={{
            color: 'black',
            fontSize: '17px',
            fontWeight: 600,
            lineHeight: '24px',
            margin: 0,
            marginBottom: '8px',
          }}
        >
          Total Cost of Ownership
        </h2>
        <div
          style={{
            color: 'black',
            fontSize: '30px',
            fontWeight: 600,
            margin: 0,
          }}
        >
          ${formatDollarAmountWithCents(total)}
        </div>
      </div>

      {/* Bar Graph - By itself */}
      <div className="w-full mb-6">
        <BarChart segments={segments} height="50px" showGaps={true} />
      </div>

      {/* Legend Items as individual rows */}
      <div className="w-full">
        {segments.map((seg) => (
          <MobileLegendItem
            key={seg.name}
            name={seg.name}
            dollarAmount={seg.dollarAmount}
            pointMultiplier={seg.pointMultiplier}
            color={seg.color}
          />
        ))}
      </div>
    </div>
  )
}

// Main Component
export default function CostBreakdownBar({ categories, isMonthlyView, setIsMonthlyView }) {
  const { total, segments } = useProcessedCategories(categories)

  return (
    <>
      {/* Desktop Layout */}
      <div className="hidden lg:block">
        <DesktopLayout categories={categories} total={total} segments={segments} isMonthlyView={isMonthlyView} setIsMonthlyView={setIsMonthlyView} />
      </div>
      {/* Mobile Layout */}
      <div className="block lg:hidden">
        <MobileLayout categories={categories} total={total} segments={segments} isMonthlyView={isMonthlyView} setIsMonthlyView={setIsMonthlyView} />
      </div>
    </>
  )
}

// PropTypes
DesktopLegendItem.propTypes = {
  name: PropTypes.string.isRequired,
  dollarAmount: PropTypes.number.isRequired,
  pointMultiplier: PropTypes.number.isRequired,
  isAbove: PropTypes.bool.isRequired,
  center: PropTypes.number.isRequired,
  legendGap: PropTypes.number,
}

DesktopLegendLine.propTypes = {
  isAbove: PropTypes.bool.isRequired,
  center: PropTypes.number.isRequired,
  legendGap: PropTypes.number.isRequired,
  barHeight: PropTypes.number.isRequired,
}

MobileLegendItem.propTypes = {
  name: PropTypes.string.isRequired,
  dollarAmount: PropTypes.number.isRequired,
  pointMultiplier: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
}

HeaderSection.propTypes = {
  total: PropTypes.number.isRequired,
  showButtonSwitch: PropTypes.bool,
  isMonthlyView: PropTypes.bool,
  setIsMonthlyView: PropTypes.func,
}

BarChart.propTypes = {
  segments: PropTypes.array.isRequired,
  height: PropTypes.string,
  showGaps: PropTypes.bool,
}

DesktopLayout.propTypes = {
  total: PropTypes.number.isRequired,
  segments: PropTypes.array.isRequired,
  isMonthlyView: PropTypes.bool.isRequired,
  setIsMonthlyView: PropTypes.func.isRequired,
}

MobileLayout.propTypes = {
  total: PropTypes.number.isRequired,
  segments: PropTypes.array.isRequired,
  isMonthlyView: PropTypes.bool.isRequired,
  setIsMonthlyView: PropTypes.func.isRequired,
}

CostBreakdownBar.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      dollarAmount: PropTypes.number.isRequired,
      pointMultiplier: PropTypes.number.isRequired,
    })
  ).isRequired,
  isMonthlyView: PropTypes.bool.isRequired,
  setIsMonthlyView: PropTypes.func.isRequired,
} 